import { useQuery } from '@tanstack/react-query';

import type { ResearchRoomView } from '@repo/contracts';

import {
  getRoomForCapture,
  getRoomForSession,
  roomKeys,
} from '../api/research-rooms';
import { heatSpots } from '../research-room/heatmap';

/** How far the legend counter-scales at most — the zoom below which it
 *  stops holding its size on screen and shrinks with the board. */
const LEGEND_MAX_SCALE = 2;

/**
 * The room's attention on one capture: a warm glow over every spot it talked
 * about — yellow at the edge, orange, red where it gathered most — with a
 * small legend saying what the colours mean.
 *
 * Reads the room from the same query the panel keeps live — one source of
 * truth, and the map fills in as the conversation does. Drawn inside the
 * capture card, so it pans and zooms with the screenshot; the legend is
 * counter-scaled to stay readable, up to `LEGEND_MAX_SCALE`.
 *
 * A capture can be talked about by two rooms: its own (the one opened on
 * it, a re-test's) and the session's chat, when it is one of the screens
 * that chat looks at. Both are read, and only what was placed on this
 * capture is drawn — a spot names its screen (`focus.captureId`), or is on
 * its room's first.
 */
export function HeatmapOverlay({
  captureId,
  sessionId,
  zoom,
}: {
  captureId: string;
  sessionId: string;
  zoom: number;
}) {
  const { data: own } = useQuery({
    queryFn: () => getRoomForCapture(captureId),
    queryKey: roomKeys.forCapture(captureId),
  });
  const { data: chat } = useQuery({
    queryFn: () => getRoomForSession(sessionId),
    queryKey: roomKeys.forSession(sessionId),
  });
  // The chat's own messages, kept live by the panel under its first screen.
  const chatScreen = chat?.room.captureId;
  const { data: chatLive } = useQuery({
    enabled: Boolean(chatScreen) && chatScreen !== captureId,
    queryFn: () => getRoomForCapture(chatScreen ?? ''),
    queryKey: roomKeys.forCapture(chatScreen ?? 'none'),
  });
  const spots = heatSpots(onCapture([own, chatLive], captureId));
  if (spots.length === 0) return null;

  return (
    <>
      <div
        aria-label="Where the room's attention landed"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-thumb duration-500 animate-in fade-in motion-reduce:animate-none"
        role="img"
      >
        {spots.map((spot) => {
          const { focus } = spot;
          // The glow reaches well past the element so neighbouring spots
          // blend into regions — what makes a heatmap read at a glance.
          const spreadX = Math.max(focus.width * 0.5, 90);
          const spreadY = Math.max(focus.height * 0.9, 90);
          const box = {
            height: focus.height + spreadY * 2,
            left: focus.x - spreadX,
            top: focus.y - spreadY,
            width: focus.width + spreadX * 2,
          };
          return (
            <div
              className="absolute"
              key={`${focus.label}-${String(focus.x)}-${String(focus.y)}`}
              style={box}
            >
              {/* The warm halo every spot has… */}
              <div
                className="absolute inset-0 bg-radial from-heat-low to-transparent to-70% mix-blend-multiply"
                style={{ opacity: 0.55 + spot.heat * 0.35 }}
              />
              {/* …and the heat at its centre: orange for a spot touched on,
                  red where the room kept coming back. */}
              <div
                className="absolute inset-0 bg-radial from-heat-high via-heat-mid via-25% to-transparent to-50% mix-blend-multiply"
                style={{ opacity: 0.25 + spot.heat * 0.75 }}
              />
            </div>
          );
        })}
      </div>

      {/* What the colours mean, at the capture's top right — counter-scaled,
          so it is the same size on screen, until the board is far enough out
          that it would cover the screenshot it explains: then it shrinks
          with the board. */}
      <div
        className="pointer-events-none absolute top-0 right-0"
        style={{
          transform: `scale(${String(Math.min(1 / zoom, LEGEND_MAX_SCALE))})`,
          transformOrigin: 'top right',
        }}
      >
        <div className="m-2 w-48 rounded-button bg-surface px-2.5 py-2 shadow-popover ring-1 ring-hairline duration-500 animate-in fade-in motion-reduce:animate-none">
          <p className="text-caption font-semibold text-text-primary">
            Where the room focused
          </p>
          <p className="mt-1 flex items-center gap-2 text-tag text-text-secondary">
            Less
            <span
              aria-hidden="true"
              className="h-1.5 flex-1 rounded-pill bg-linear-to-r from-heat-low via-heat-mid to-heat-high"
            />
            More
          </p>
          <p className="mt-1 text-tag leading-snug text-pretty text-text-muted">
            From {spots.length} {spots.length === 1 ? 'spot' : 'spots'} the
            personas pointed at
          </p>
        </div>
      </div>
    </>
  );
}

/** Every message placed on `captureId`, across the rooms given, once. */
function onCapture(
  views: (ResearchRoomView | null | undefined)[],
  captureId: string,
) {
  const seen = new Set<string>();
  return views.flatMap((view) =>
    (view?.messages ?? []).filter((message) => {
      if (seen.has(message.id)) return false;
      seen.add(message.id);
      const on = message.focus?.captureId ?? view?.room.captureId;
      return on === captureId;
    }),
  );
}
